/**
 * Query block for GraphQL based on the model being requests
 */

import { gql, DocumentNode } from '@apollo/client';


/**
 * Get the variables to send along the GraphQL query
 * @param {object} params 
 * @returns 
 */
export const paramsToGraphQLVariables = (params: HazardInputs): HazardParameters => {
  if (params.siteDesignation === 'XV' && params.siteDesignationXV) {
    return {
      code: params.code,
      latitude: Number(params.latitude),
      longitude: Number(params.longitude),
      vs30: Number(params.siteDesignationXV),
    }
  } else if (params.siteDesignation === 'XS' && params.siteDesignationXS) {
    return {
      code: params.code,
      latitude: Number(params.latitude),
      longitude: Number(params.longitude),
      siteClass: params.siteDesignationXS,
    }
  }
  throw new Error('Invalid parameter sent to web form, require XS or XV and associated value to be defined')
}

/**
 * Determine the type of GraphQL query based on the site designation type
 * @param {object} params 
 * @returns 
 */
export const paramsToGraphQLQuery = (params: HazardInputs): DocumentNode => {
  let graphql_object_type = 'CanSHM6';
  let graphql_query = 'canSHM6';
  switch(params.code) {
    case 'canSHM6':
    case 'nbc2020':
      graphql_object_type = 'CanSHM6';
      graphql_query = 'canSHM6';
      break;
    case 'canSHM6P1':
    case 'nbc2025':
    default:
      graphql_object_type = 'CanSHM6P1';
      graphql_query = 'canSHM6P1';
  }

  const siteDesignationFragment = gql`
  fragment siteDesignationFragment on ${graphql_object_type}SiteDesignation {
    vs30
    sa0p05
    sa0p1
    sa0p2
    sa0p3
    sa0p5
    sa1p0
    sa2p0
    sa5p0
    sa10p0
    poe50
    foe
    pga
    pgv
  }
`

const codeFragment = gql`
  fragment codeFragment on ${graphql_object_type}Point {
    metadata: metadata {
      zones
    }
    X140: siteDesignationsXv(vs30: 140) {
      ...siteDesignationFragment
    }
    X160: siteDesignationsXv(vs30: 160) {
      ...siteDesignationFragment
    }
    X180: siteDesignationsXv(vs30: 180) {
      ...siteDesignationFragment
    }
    X250: siteDesignationsXv(vs30: 250) {
      ...siteDesignationFragment
    }
    X300: siteDesignationsXv(vs30: 300) {
      ...siteDesignationFragment
    }
    X360: siteDesignationsXv(vs30: 360) {
      ...siteDesignationFragment
    }
    X450: siteDesignationsXv(vs30: 450) {
      ...siteDesignationFragment
    }
    X580: siteDesignationsXv(vs30: 580) {
      ...siteDesignationFragment
    }
    X760: siteDesignationsXv(vs30: 760) {
      ...siteDesignationFragment
    }
    X910: siteDesignationsXv(vs30: 910) {
      ...siteDesignationFragment
    }
    X1100: siteDesignationsXv(vs30: 1100) {
      ...siteDesignationFragment
    }
    X1500: siteDesignationsXv(vs30: 1500) {
      ...siteDesignationFragment
    }
    X1600: siteDesignationsXv(vs30: 1600) {
      ...siteDesignationFragment
    }
    X2000: siteDesignationsXv(vs30: 2000) {
      ...siteDesignationFragment
    }
    X3000: siteDesignationsXv(vs30: 3000) {
      ...siteDesignationFragment
    }
    XE: siteDesignationsXs(siteClass: E) {
      ...siteDesignationFragment
    }
    XD: siteDesignationsXs(siteClass: D) {
      ...siteDesignationFragment
    }
    XC: siteDesignationsXs(siteClass: C) {
      ...siteDesignationFragment
    }
    XB: siteDesignationsXs(siteClass: B) {
      ...siteDesignationFragment
    }
    XA: siteDesignationsXs(siteClass: A) {
      ...siteDesignationFragment
    }
  }

  ${siteDesignationFragment}
`

// Create GraphQL query used for Xv site designation
const queryCodeHazardXv = gql`
  query Hazard($latitude: Float!, $longitude: Float!, $vs30: Float!) {
    canshm: ${graphql_query}(latitude: $latitude, longitude: $longitude) {
      userSpecified: siteDesignationsXv(vs30: $vs30) {
        ...siteDesignationFragment
      }
      ...codeFragment
    }
  }
  ${codeFragment}
`;

// Create GraphQL query used for Xs site designation
const queryCodeHazardXs = gql`
  query Hazard($latitude: Float!, $longitude: Float!, $siteClass: ${graphql_object_type}SiteClass!) {
    canshm: ${graphql_query}(latitude: $latitude, longitude: $longitude) {
      userSpecified: siteDesignationsXs(siteClass: $siteClass) {
        ...siteDesignationFragment
      }
      ...codeFragment
    }
  }
  ${codeFragment}
`;


  return (params.siteDesignation === 'XV') ? queryCodeHazardXv : queryCodeHazardXs;
}